import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PendingDocuments } from 'src/app/shared/models/documents.model';
import { ContractsService } from 'src/app/shared/services/contracts.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { UsersService } from '../../services/users.service';

@Component({
    selector: 'app-pendences-modal',
    templateUrl: './pendences-modal.component.html',
    styleUrls: ['./pendences-modal.component.scss'],
    standalone: false
})
export class PendencesModalComponent implements AfterViewInit, OnChanges {
	private documentsToUpload: File[] = [];
	private ngUnsubscribe = new Subject<void>();

	public isMobile: boolean = false;
	public documents: PendingDocuments = {};
	public documentsSuccess = false;
	public documentsTitle = '';
	public documentsText = '';
	public isPending = false;
	public userType: string = 'PF';
	public userId: any = '';

	@Input() public user: any;
	@Input() public contract!: any;
	@Input() public showModal = false;
	@Input() public signedContract = false;
	@Input() public userDocument?: any;
	@Input() public rejectionReason?: string;

	constructor(
		private userAgent: UserAgentService,
		private router: Router,
		private contractsService: ContractsService,
		private ref: ChangeDetectorRef,
		private usersService: UsersService
	) {
		ModalService.closeModal.subscribe(() => {
			this.showModal = false;
			this.documentsTitle = '';
			this.documentsText = '';
		});
		this.isMobile = userAgent.verifyMobile();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Cuando se abre el modal (showModal cambia a true), recargar los datos del usuario
		if (changes['showModal'] && changes['showModal'].currentValue === true) {
			this.refreshUserData();
		}

		// Si cambia el user input, actualizar userId si es necesario
		if (changes['user'] && changes['user'].currentValue) {
			const newUser = changes['user'].currentValue;
			if (newUser.id && !this.userId) {
				this.userId = newUser.id;
			}
		}
	}

	ngAfterViewInit(): void {
		if (this.user) {
			this.user?.clientType === 1 ? (this.userType = 'PJ') : 'PF';
			// Si tenemos user pero no userId, establecerlo
			if (this.user.id && !this.userId) {
				this.userId = this.user.id;
			}
		}

		if (this.showModal && this.contract && this.contract?.type == 'DOCUMENTACIÓN') {
			this.getPendingDocuments();
		}

		if (this.signedContract) {
			this.documentsTitle = 'Contrato firmado';
			this.documentsText = 'Tu contrato tiene todas las firmas! Continúa para el pago:';
		}

		if (this.userDocument) this.getUserByDocument();

		this.ref.detectChanges();
	}

	private refreshUserData(): void {
		// Determinar el userId a usar
		const userIdToLoad = this.userId || this.user?.id || this.user?.authContext?.id;

		if (userIdToLoad) {
			// Recargar los datos del usuario para obtener blobFiles actualizados
			this.loadUserData(userIdToLoad);
		} else if (this.userDocument) {
			// Si no tenemos userId pero tenemos userDocument, obtener el usuario por documento
			this.getUserByDocument();
		}
	}

	private getUserByDocument() {
		this.usersService
			.getUserByDocument(this.userDocument)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (res: any) => {
					this.userId = res.data.id;
					// Cargar el objeto user completo con blobFiles
					this.loadUserData(res.data.id);
				},
				error: (err: any) => {},
			});
	}

	private loadUserData(userId: string | number): void {
		this.usersService
			.getUserById(userId)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (res: any) => {
					if (res?.data) {
						// IMPORTANTE: Crear una nueva referencia del array para que Angular detecte el cambio
						// Si mantenemos la misma referencia, ngOnChanges no se dispara en los hijos
						const newBlobFiles = res.data?.blobFiles ? [...res.data.blobFiles] : [];
						
						// Actualizar el objeto user con los datos más recientes
						this.user = {
							...res.data,
							blobFiles: newBlobFiles
						};

						// Actualizar userType si es necesario
						if (this.user?.clientType === 1) {
							this.userType = 'PJ';
						} else {
							this.userType = 'PF';
						}

						// Asegurar que userId esté establecido
						if (this.user.id && !this.userId) {
							this.userId = this.user.id;
						}

						// Forzar múltiples detecciones de cambios para asegurar que Angular actualice el getter y los hijos
						this.ref.detectChanges();
						
						// Forzar una segunda detección después de un micro-delay
						setTimeout(() => {
							this.ref.detectChanges();
						}, 0);
					}
				},
				error: (err: any) => {
					console.error('❌ [PendencesModal] Error al cargar datos del usuario:', err);
				},
			});
	}

	private getPendingDocuments(): void {
		this.isPending = true;
		this.contractsService
			.getPendingDocuments(this.contract?.contract?.id)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (res: any) => {
					this.isPending = false;
					this.documents = res.data;
					// Forzar detección de cambios para recalcular isDisabled
					this.ref.detectChanges();
				},
				error: (err: any) => {
					console.error('❌ [PendencesModal] Error en getPendingDocuments', err);
					if (err.includes('enviados ainda estão sendo processados')) {
						this.documentsSuccess = true;
						this.documentsTitle = '¡Documentos recibidos!';
						this.documentsText = 'Pronto recibirás un correo electrónico con el estado de tu solicitud. También <br />puedes revisar el estado de contratación.';
					}
					this.isPending = false;
				},
			});
	}

	@HostListener('window:resize')
	public onResize(): void {
		this.isMobile = this.userAgent.verifyMobile();
	}

	public get currentDescription(): string {
		switch (this.contract?.type) {
			case 'ANÁLISIS':
				return '¡Estamos analizando tu pedido!';
			case 'ASSINATURA':
				return 'Completa las firmas para proceder con tu contratación.';
			case 'ASSINATURA_PJ':
				return 'El contrato fue enviado a tu correo electrónico para ser firmado';
			case 'DOCUMENTACIÓN':
				return 'Requerimos los siguientes documentos:';
			case 'PAGO':
				return 'Tu contrato está firmado<br /> Continúa con el pago:';
			default:
				return '¡Estamos analizando tu pedido!';
		}
	}

	public get currentStep(): number {
		switch (this.contract?.type) {
			case 'ANÁLISIS':
				return 0;
			case 'ASSINATURA':
				return 1;
			case 'ASSINATURA_PJ':
				return 1;
			case 'DOCUMENTACIÓN':
				return 1;
			case 'PAGO':
				return 2;
			default:
				return 0;
		}
	}

	public get rejectionReasonText(): string {
		return (this.documents?.observation || this.rejectionReason || '').trim();
	}

	private isIntegratedFile(file: any): boolean {
		const integratedAt = file?.integratedAt ?? file?.IntegratedAt;
		return !!integratedAt;
	}

	public get isDisabled(): boolean {
		if (this.contract?.type !== 'DOCUMENTACIÓN') return false;

		const pendingDocuments = this.documents?.pendingDocuments ?? [];
		const blobFiles = this.user?.blobFiles ?? [];

		if (pendingDocuments.length === 0) return true;

		const availableFiles = blobFiles.filter((file: any) =>
			file?.type !== undefined && file?.type !== null && !this.isIntegratedFile(file)
		);
			return pendingDocuments.some((pendingDoc: any) => {
			const matched = availableFiles.some((file: any) =>
				file.type === pendingDoc.type &&
				(pendingDoc.documentId == null || file.pendencyId === pendingDoc.documentId)
			);
			return !matched;
		});
	}

public handleDocumentsModal(event: any): void {
	this.documentsToUpload.push(event);
	// Ya no necesitamos actualizar isDisabled manualmente, se calcula automáticamente como getter
	// basándose en user.blobFiles y documents.pendingDocuments
	this.ref.detectChanges();
}

public handleFileDeleted(fileEvent: { fileName: string; blobFileId: number | null }): void {
	// Recargar los datos del usuario para sincronizar con el backend
	const userIdToLoad = this.userId || this.user?.id || this.user?.authContext?.id;

	if (userIdToLoad) {
		// Delay de 500ms para asegurar que el backend terminó de eliminar
		setTimeout(() => {
			this.loadUserData(userIdToLoad);
		}, 500);
	} else if (this.user && this.user.blobFiles && Array.isArray(this.user.blobFiles)) {
		// Fallback: Remover el archivo eliminado del array blobFiles localmente
		this.user.blobFiles = this.user.blobFiles.filter((blobFile: any) => {
			// Comparar por id si está disponible
			if (fileEvent.blobFileId && blobFile.id) {
				return blobFile.id !== fileEvent.blobFileId;
			}
			// Si no hay id, comparar por nombre del archivo extraído de la URL
			if (blobFile.url) {
				const blobFileName = this.extractFileNameFromUrl(blobFile.url);
				return blobFileName !== fileEvent.fileName;
			}
			return true;
		});
		this.ref.detectChanges();
	}
}

	public handleFileUploaded(event: { fileName: string; uri: string; type: number }): void {
		// Prioridad 1: Recargar los datos del usuario si tenemos userId (más confiable)
		const userIdToLoad = this.userId || this.user?.id || this.user?.authContext?.id;

		if (userIdToLoad) {
			// Múltiples reintentos para asegurar que el backend terminó de guardar
			let attempts = 0;
			const maxAttempts = 3;

			const tryLoadData = () => {
				attempts++;

				this.usersService.getUserById(userIdToLoad)
					.pipe(takeUntil(this.ngUnsubscribe))
					.subscribe({
						next: (res: any) => {
							if (res?.data) {

								const uploadedFile = res.data.blobFiles?.find((bf: any) =>
									bf.type === event.type &&
									(bf.url === event.uri || bf.url?.includes(event.fileName))
								);

								const hasPendencyId = uploadedFile?.pendencyId || uploadedFile?.PendencyId;

								this.user = res.data;
								if (this.user?.clientType === 1) {
									this.userType = 'PJ';
								} else {
									this.userType = 'PF';
								}

								this.ref.detectChanges();

								if (!hasPendencyId && attempts < maxAttempts) {
									setTimeout(tryLoadData, 300);
								} else {
									setTimeout(() => this.ref.detectChanges(), 0);
								}
							}
						},
						error: (err: any) => {
							console.error(`❌ [PendencesModal] Error al recargar (intento ${attempts}):`, err);
							if (attempts < maxAttempts) {
								setTimeout(tryLoadData, 300);
							}
						}
					});
			};

			setTimeout(tryLoadData, 500);
		} else if (this.user && this.user.blobFiles && Array.isArray(this.user.blobFiles)) {
			// Prioridad 2: Si no podemos recargar, agregar el archivo localmente
			// Construir el objeto blobFile con la información disponible
			const newBlobFile: any = {
				url: event.uri,
				type: event.type,
				fileName: event.fileName
			};

			// Verificar si el archivo ya existe para evitar duplicados
			const exists = this.user.blobFiles.some((blobFile: any) => {
				if (blobFile.url === event.uri) return true;
				if (blobFile.fileName === event.fileName) return true;
				if (blobFile.url) {
					const blobFileName = this.extractFileNameFromUrl(blobFile.url);
					return blobFileName === event.fileName;
				}
				return false;
			});

			if (!exists) {
				this.user.blobFiles.push(newBlobFile);
				this.ref.detectChanges();
			}
		}
	}

	private extractFileNameFromUrl(url: string): string {
		try {
			const containerPath = 'ondemand-v3/';
			const containerIndex = url.indexOf(containerPath);
			if (containerIndex !== -1) {
				const fileName = url.substring(containerIndex + containerPath.length);
				return fileName || '';
			}
			const urlParts = url.split('/');
			return urlParts[urlParts.length - 1] || '';
		} catch (error) {
			return '';
		}
	}

	public handleContinue(): void {
		if (this.contract) {
			if (this.contract?.type == 'FIRMA') {
				this.signedContract ? this.router.navigate([this.contract?.contract?.needsToSetInsuranceBeneficiary ? '/area-logada/cadastro-beneficiario/' : '/area-logada/pagamento/', this.contract?.contract?.id]) : this.router.navigate(['/area-logada/assinar-contrato/', this.contract?.contract?.id]);
			}
			if (this.contract?.type == 'PAGO') {
				this.contract?.contract?.needsToSetInsuranceBeneficiary ? this.router.navigate(['/area-logada/cadastro-beneficiario/', this.contract?.contract?.id]) : this.router.navigate(['/area-logada/pagamento/', this.contract?.contract?.id]);
			}
			if (this.contract?.type == 'DOCUMENTACIÓN') {
				// Recargar los datos del usuario antes de enviar para asegurar que tenemos los PendencyId actualizados
				const userIdToLoad = this.userId || this.user?.id || this.user?.authContext?.id;
				
				if (userIdToLoad) {
					this.isPending = true;
					// Recargar los datos del usuario primero
					this.usersService
						.getUserById(userIdToLoad)
						.pipe(takeUntil(this.ngUnsubscribe))
						.subscribe({
							next: (res: any) => {
								if (res?.data) {
									// Actualizar el objeto user con los datos más recientes
									this.user = res.data;
									
									// Ahora enviar la solicitud con los datos actualizados
									this.savePendingDocuments();
								} else {
									// Si no hay datos, intentar enviar de todas formas
									console.warn('⚠️ [PendencesModal] handleContinue sin datos de usuario al recargar, intentando savePendingDocuments igualmente');
									this.savePendingDocuments();
								}
							},
							error: (err: any) => {
								// Si falla la recarga, intentar enviar de todas formas
								console.error('❌ [PendencesModal] Error al recargar datos del usuario antes de savePendingDocuments', err);
								this.savePendingDocuments();
							},
						});
				} else {
					// Si no tenemos userId, enviar directamente
					console.warn('⚠️ [PendencesModal] handleContinue sin userId, llamando savePendingDocuments directo');
					this.savePendingDocuments();
				}
			}
		}
	}

	private savePendingDocuments(): void {
		this.isPending = true;
		this.contractsService
			.savePendingDocuments(this.contract?.contract?.id)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (res: any) => {
					this.documentsSuccess = true;
					this.documentsTitle = '¡Documentos recibidos!';
					this.documentsText = 'Pronto recibirás un correo electrónico con el estado de tu solicitud. También<br /> puedes revisar el estado de tu pedido en <strong>"Contratos pendientes"</strong>';
					this.isPending = false;
				},
				error: (err: any) => {
					console.error('❌ [PendencesModal] Error en savePendingDocuments', err);
					this.isPending = false;
				},
			});
	}

	public stepIcon(index: number): string {
		if (index < this.currentStep) {
			return this.isMobile ? '/assets/icons/svg/past-step-mobile.svg' : '/assets/icons/svg/past-step.svg';
		} else if (index == this.currentStep) {
			return this.isMobile ? '/assets/icons/svg/current-step-mobile.svg' : '/assets/icons/svg/current-step.svg';
		}

		return this.isMobile ? '/assets/icons/svg/future-step-mobile.svg' : '/assets/icons/svg/future-step.svg';
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}
}
